import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AuthContext } from "../../context/AuthContext";
import useAxios from "../../hooks/useAxios";
import AlertModal from "../../components/common/alertModal/AlertModal";
import Header from "../../components/Header/Header";
import { useNavigate } from "react-router-dom";
import "./PurchasePlanPage.css";

const PurchasePlanPage = () => {
  const { role } = useContext(AuthContext);
  const axiosInstance = useAxios();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    type: "success",
  });

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/plans/pricing");
      if (res.data.success) {
        setPlans(res.data.plans);
      } else {
        setError("Failed to fetch pricing.");
      }
    } catch (err) {
      setError("Error fetching pricing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSelectPlan = (planType) => {
    setSelectedPlan(planType);
    setShowConfirm(true);
  };

  const handleConfirmPurchase = async () => {
    setConfirmLoading(true);
    try {
      const res = await axiosInstance.post(
        role === "family" ? "/checkout/family" : "/checkout/teacher",
        {
          billing_cycle: selectedPlan,
        }
      );

      if (res.data.success && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setAlert({
          open: true,
          title: "Error",
          message: res.data.message || "Failed to create checkout session.",
          type: "error",
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        title: "Error",
        message: err.response?.data?.message || "Error creating checkout session.",
        type: "error",
      });
    } finally {
      setConfirmLoading(false);
      setShowConfirm(false);
    }
  };

  const getPlanDetails = (planType) => {
    if (role === "teacher") {
      return planType === "monthly"
        ? [
            "Up to 360 students",
            "Create classes to organize students",
            "Unlimited assessments",
            "Personalized experience for all students",
            "Auto-renewed (cancel anytime)"
          ]
        : [
            "25% discount",
            "Up to 360 students",
            "Create classes to organize students",
            "Unlimited assessments",
            "Personalized experience for all students",
            "Auto-renewed (cancel anytime)"
          ];
    } else {
      return planType === "monthly"
        ? [
            "Up to 8 students",
            "Unlimited assessments",
            "Track children's education process",
            "Personalized experience for all students",
            "Auto-renewed (cancel anytime)"
          ]
        : [
            "25% discount",
            "Up to 8 students",
            "Unlimited assessments",
            "Track children's education process",
            "Personalized experience for all students",
            "Auto-renewed (cancel anytime)"
          ];
    }
  };

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography className="error-text">{error}</Typography>;
  }

  return (
    <>
      <Header navLinks={[]} />
        
        <Box className="purchase-plan-page page-content">
          <Typography variant="h4" className="page-title">
            Choose Your Subscription Plan
          </Typography>
          <Grid container spacing={8} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Card className="plan-card">
                <CardContent>
                  <Avatar className="plan-avatar">M</Avatar>
                  <Typography variant="h5" className="plan-title">
                    Monthly Plan
                  </Typography>
                  <Divider className="divider" />
                  <Typography variant="h3" className="plan-cost">
                    AED {plans.monthly}
                  </Typography>
                  <List>
                    {getPlanDetails("monthly").map((detail, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircleIcon className="check-icon"  />
                        </ListItemIcon>
                        <ListItemText primary={detail} className="plan-description-detail"/>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    className="choose-button secondary-button"
                    onClick={() => handleSelectPlan("monthly")}
                  >
                    Choose Monthly
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card className="plan-card">
                <CardContent>
                  <Avatar className="plan-avatar">Y</Avatar>
                  <Typography variant="h5" className="plan-title">
                    Yearly Plan
                  </Typography>
                  <Divider className="divider" />
                  <Typography variant="h3" className="plan-cost">
                    AED {plans.yearly}
                  </Typography>
                  <List>
                    {getPlanDetails("yearly").map((detail, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircleIcon className="check-icon" />
                        </ListItemIcon>
                        <ListItemText primary={detail} className="plan-description-detail"/>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    className="choose-button secondary-button"
                    onClick={() => handleSelectPlan("yearly")}
                  >
                    Choose Yearly
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
          {role === "teacher" && (
            <Box textAlign="center" mb={4} className="mt-5">
              <Typography variant="h6" className="school-question">
                Are you part of a school with an active subscription on our platform?
              </Typography>
              <Button
                variant="outlined"
                className="skip-payment-button"
                onClick={() => navigate("/setup-profile", { state: { is_school_required: true } })}
                >
                Skip Payment & Setup Profile
              </Button>
            </Box>
          )}

          <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
            <DialogTitle className="dialog-title">Confirm Your Plan</DialogTitle>
            <DialogContent className="dialog-content">
              <Typography variant="h6" className="dialog-plan-name">
                Selected Plan: <strong>{selectedPlan?.charAt(0).toUpperCase() + selectedPlan?.slice(1)} Plan</strong>
              </Typography>
              <Typography variant="body1" className="dialog-cost">
                Cost: <strong>AED {selectedPlan === "monthly" ? plans.monthly : plans.yearly}</strong>
              </Typography>
              <Typography variant="body2" className="dialog-auto-renew">
                This plan will renew automatically. You can cancel anytime from your dashboard.
              </Typography>
              <Typography variant="body2" className="dialog-note">
                By clicking "Proceed to Payment," you will be redirected to perform the online payment process.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConfirm(false)} className="cancel-button">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmPurchase}
                className="confirm-button"
                disabled={confirmLoading}
              >
                {confirmLoading ? <CircularProgress size={24} /> : "Proceed to Payment"}
              </Button>
            </DialogActions>
          </Dialog>


          <AlertModal
            open={alert.open}
            onClose={() => setAlert({ ...alert, open: false })}
            title={alert.title}
            message={alert.message}
            type={alert.type}
          />
      </Box>
    </>
  );
};

export default PurchasePlanPage;
